import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { useRsvp } from '@/src/modules/rsvp';

interface RSVPAnniversarySectionProps {
  invitationId: string | null;
  isLive: boolean;
  language: 'en' | 'mr';
  couple: any;
}

export const RSVPAnniversarySection: React.FC<RSVPAnniversarySectionProps> = ({
  invitationId,
  isLive,
  language,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [attending, setAttending] = useState<boolean | null>(null);
  const [guests, setGuests] = useState('1');
  const [message, setMessage] = useState('');

  const { state, errorMessage, submit } = useRsvp(invitationId, isLive);
  const isSubmitting = state === 'loading';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || attending === null || isSubmitting) return;
    const headcount = Math.min(10, Math.max(1, parseInt(guests, 10) || 1));
    await submit({
      guestName: name.trim(),
      guestEmail: email.trim() || undefined,
      status: attending ? 'ACCEPTED' : 'DECLINED',
      headcount: attending ? headcount : 1,
      message: message.trim() || undefined,
    });
  };

  if (state === 'success') {
    return (
      <section className="relative py-24 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-block mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-rose-400 rounded-full flex items-center justify-center animate-bounce">
              <Heart size={40} className="text-white" fill="currentColor" />
            </div>
          </div>
          <h2 className="text-4xl font-black text-amber-200 mb-4">
            {language === 'mr' ? 'धन्यवाद!' : 'Thank You!'}
          </h2>
          <p className="text-gray-300 text-lg">
            {language === 'mr'
              ? 'आपले RSVP यशस्वीरित्या प्राप्त झाले। आमच्या विशेष दिवसमध्ये आपली उपस्थिती आमांना खूप मोलाची आहे।'
              : "We're honored to have you celebrate with us!"}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-24 px-4 bg-gradient-to-b from-transparent via-rose-900/20 to-transparent">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-center text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-rose-400 mb-12">
          {language === 'mr' ? '💕 आमच्यासाथी असा' : '💕 Join Us'}
        </h2>

        <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-md rounded-3xl border-2 border-amber-400/30 p-10 shadow-2xl space-y-6">
          <div>
            <label className="block text-sm font-bold text-amber-100 mb-3">
              {language === 'mr' ? 'नाव' : 'Full Name'} *
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white/10 border-2 border-amber-400/30 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-rose-400 focus:outline-none"
              placeholder={language === 'mr' ? 'आपले नाव' : 'Your name'}
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-amber-100 mb-3">
              {language === 'mr' ? 'ईमेल' : 'Email'}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/10 border-2 border-amber-400/30 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-rose-400 focus:outline-none"
              placeholder={language === 'mr' ? 'ईमेल' : 'your@email.com'}
            />
          </div>

          <div>
            <p className="text-sm font-bold text-amber-100 mb-4">
              {language === 'mr' ? 'आप आ शकता?' : 'Will you attend?'} *
            </p>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setAttending(true)}
                className={`py-4 px-6 rounded-lg font-bold transition-all ${
                  attending === true
                    ? 'bg-gradient-to-r from-amber-400 to-rose-400 text-white shadow-lg shadow-rose-500/50'
                    : 'bg-white/10 border-2 border-gray-500 text-gray-300 hover:border-amber-400'
                }`}
              >
                💕 {language === 'mr' ? 'होय' : 'Yes'}
              </button>
              <button
                type="button"
                onClick={() => setAttending(false)}
                className={`py-4 px-6 rounded-lg font-bold transition-all ${
                  attending === false
                    ? 'bg-gradient-to-r from-gray-600 to-gray-700 text-white shadow-lg'
                    : 'bg-white/10 border-2 border-gray-500 text-gray-300 hover:border-gray-400'
                }`}
              >
                💔 {language === 'mr' ? 'नहीं' : 'No'}
              </button>
            </div>
          </div>

          {attending === true && (
            <div>
              <label className="block text-sm font-bold text-amber-100 mb-3">
                {language === 'mr' ? 'कितने लोग आएंगे?' : 'Number of Guests'}
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={guests}
                onChange={(e) => setGuests(e.target.value)}
                className="w-full bg-white/10 border-2 border-amber-400/30 rounded-lg px-4 py-3 text-white focus:border-rose-400 focus:outline-none"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-amber-100 mb-3">
              {language === 'mr' ? 'संदेश' : 'Special Message'}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="w-full bg-white/10 border-2 border-amber-400/30 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-rose-400 focus:outline-none resize-none"
              placeholder={language === 'mr' ? 'संदेश छोडा...' : 'Leave a message...'}
            />
          </div>

          {state === 'error' && errorMessage && (
            <p className="text-sm text-center font-semibold text-red-400">{errorMessage}</p>
          )}

          <button
            type="submit"
            disabled={attending === null || isSubmitting}
            className="w-full bg-gradient-to-r from-amber-400 to-rose-400 text-white font-bold py-4 rounded-lg hover:shadow-lg hover:shadow-rose-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            💕 {isSubmitting
              ? (language === 'mr' ? 'सबमिट करत आहे...' : 'Submitting...')
              : (language === 'mr' ? 'RSVP सबमिट करा' : 'Confirm RSVP')}
          </button>
        </form>
      </div>
    </section>
  );
};
